'use client';

import React, { useRef } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import LottieRender from './LottieRender';

gsap.registerPlugin(ScrollTrigger);

const MobileOurJourney = () => {
    const containerRef = useRef<HTMLDivElement>(null);
    const lineRef = useRef<HTMLDivElement>(null);

    useGSAP(() => {
        // Timeline Line Animation
        gsap.fromTo(lineRef.current,
            { height: '0%' },
            {
                height: '100%',
                ease: 'none',
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: 'top 60%',
                    end: 'bottom 80%',
                    scrub: 1
                }
            }
        );

        // Milestones Animation
        const milestones = gsap.utils.toArray<HTMLElement>('.milestone-card');
        milestones.forEach((card, i) => {
            gsap.from(card, {
                opacity: 0,
                x: 30,
                scrollTrigger: {
                    trigger: card,
                    start: 'top 85%',
                    end: 'top 60%',
                    scrub: 1
                }
            });

            // Activate dot animation
            const dot = card.previousElementSibling;
            gsap.to(dot, {
                backgroundColor: '#4f46e5', // indigo-600
                boxShadow: '0 0 15px rgba(79, 70, 229, 0.6)',
                scrollTrigger: {
                    trigger: card,
                    start: 'top 60%',
                    toggleActions: 'play none none reverse'
                }
            });
        });

    }, { scope: containerRef });

    return (
        <section ref={containerRef} className="py-20 bg-slate-50 relative overflow-hidden">

            {/* Sticky Lottie Background - Fades out at bottom */}
            <div className="absolute top-0 right-0 w-[80%] h-[50vh] opacity-10 pointer-events-none">
                <LottieRender animationPath="/animations/journey_v2.json" />
            </div>

            <div className="px-6 relative z-10">
                <div className="mb-12">
                    <span className="text-xs font-bold text-indigo-600 tracking-[0.2em] uppercase block mb-3">
                        Our History
                    </span>
                    <h2 className="text-4xl font-black text-slate-900 leading-tight">
                        A Journey of <br />
                        <span className="text-indigo-600">Evolution</span>
                    </h2>
                </div>

                <div className="relative pl-4">
                    {/* The Path Line */}
                    <div className="absolute left-[23px] top-2 bottom-0 w-[2px] bg-slate-200">
                        <div ref={lineRef} className="w-full bg-indigo-600 shadow-[0_0_10px_rgba(79,70,229,0.5)]" />
                    </div>

                    <div className="space-y-12">
                        {[
                            { year: "2023", title: "Inception", desc: "Founded with a vision to merge code and SEO." },
                            { year: "2024", title: "Rapid Expansion", desc: "Scaled to 25+ enterprise clients and launched core platforms." },
                            { year: "2025", title: "Global Reach", desc: "Establishing international presence and AI integration." }
                        ].map((milestone, i) => (
                            <div key={i} className="relative pl-8">
                                {/* Timeline Dot */}
                                <div className="absolute left-0 top-2 w-4 h-4 rounded-full border-2 border-slate-200 bg-white z-10 transition-colors duration-500" />

                                {/* Card */}
                                <div className="milestone-card bg-white rounded-2xl p-6 shadow-lg shadow-slate-200/50 border border-slate-100 relative overflow-hidden">
                                    {/* Year Badge */}
                                    <div className="absolute -right-4 -top-4 w-24 h-24 bg-indigo-50 rounded-full blur-2xl opacity-50" />

                                    <span className="inline-block px-3 py-1 bg-indigo-50 text-indigo-700 text-xs font-bold rounded-full mb-3">
                                        {milestone.year}
                                    </span>

                                    <h3 className="text-xl font-bold text-slate-900 mb-2">
                                        {milestone.title}
                                    </h3>
                                    <p className="text-slate-600 text-sm leading-relaxed font-medium">
                                        {milestone.desc}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default MobileOurJourney;
